// ============================================
// CLÔTURE DE CAISSE - Interface Premium
// 4 zones distinctes, responsive, professionnel
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { 
    CreditCard, Smartphone, Banknote, Calculator,
    CheckCircle, AlertTriangle, RefreshCw, AlertCircle, 
    Coins, Wallet, TrendingUp, TrendingDown, Minus,
    DollarSign, PiggyBank, Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const CashClosingPage = () => {
    const { api, user } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Comptage des espèces - Pièces
    const [coins, setCoins] = useState({
        c25: 0,
        c50: 0,
        c100: 0,
        c200: 0
    });
    
    // Comptage des espèces - Billets
    const [bills, setBills] = useState({
        b500: 0,
        b1000: 0,
        b2000: 0,
        b5000: 0,
        b10000: 0
    });
    
    const [fondDeCaisse, setFondDeCaisse] = useState(50000);
    const [notes, setNotes] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = selectedEstablishmentId 
                ? `?establishment_id=${selectedEstablishmentId}` 
                : '';
            
            const summaryRes = await api().get(`/cash-register/summary${params}`);
            setSummary(summaryRes.data || summaryRes);
        } catch (error) {
            console.error('Error fetching data:', error);
            setSummary({
                breakdown: {
                    cash: { amount: 0, formatted: '0 F', count: 0 },
                    card: { amount: 0, formatted: '0 F', count: 0 },
                    airtel_money: { amount: 0, formatted: '0 F', count: 0 },
                    moov_money: { amount: 0, formatted: '0 F', count: 0 },
                    mobile_money: { amount: 0, formatted: '0 F', count: 0 }
                }
            });
        } finally {
            setLoading(false);
        }
    }, [api, selectedEstablishmentId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ============================================
    // CALCULS
    // ============================================
    
    const getTotalCoins = () => {
        return coins.c25 * 25 + coins.c50 * 50 + coins.c100 * 100 + coins.c200 * 200;
    };

    const getTotalBills = () => {
        return bills.b500 * 500 + bills.b1000 * 1000 + bills.b2000 * 2000 + 
               bills.b5000 * 5000 + bills.b10000 * 10000;
    };

    const getTotalCash = () => getTotalCoins() + getTotalBills();

    const getTheoreticalCash = () => {
        const ventesEspeces = summary?.breakdown?.cash?.amount || 0;
        return fondDeCaisse + ventesEspeces;
    };

    const getVariance = () => getTotalCash() - getTheoreticalCash();

    const getCardTotal = () => summary?.breakdown?.card?.amount || 0;
    const getAirtelTotal = () => summary?.breakdown?.airtel_money?.amount || summary?.breakdown?.mobile_money?.amount * 0.5 || 0;
    const getMoovTotal = () => summary?.breakdown?.moov_money?.amount || summary?.breakdown?.mobile_money?.amount * 0.5 || 0;

    const formatCFA = (amount) => `${(amount || 0).toLocaleString('fr-FR')} F`;

    const getVarianceStatus = () => {
        const v = Math.abs(getVariance());
        if (v === 0) return { status: 'exact', label: 'Caisse exacte', color: 'emerald' };
        if (v <= 500) return { status: 'minor', label: getVariance() > 0 ? 'Léger excédent' : 'Léger manquant', color: 'emerald' };
        if (v <= 2000) return { status: 'warning', label: getVariance() > 0 ? 'Excédent' : 'Manquant', color: 'orange' };
        return { status: 'critical', label: getVariance() > 0 ? 'Excédent important' : 'Manquant important', color: 'red' };
    };

    const hasCountedSomething = () => {
        return getTotalCoins() > 0 || getTotalBills() > 0;
    };

    // ============================================
    // ACTIONS
    // ============================================

    const handleClosing = async () => {
        setSubmitting(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            await api().post('/cash-register/close', {
                date: today,
                cash_count: { ...coins, ...bills },
                fond_de_caisse: fondDeCaisse,
                card_total: getCardTotal(),
                airtel_total: getAirtelTotal(),
                moov_total: getMoovTotal(),
                theoretical_cash: getTheoreticalCash(),
                actual_cash: getTotalCash(),
                variance: getVariance(),
                notes
            });
            toast.success('Clôture enregistrée avec succès !');
            setConfirmDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
        } finally {
            setSubmitting(false);
        }
    };

    // ============================================
    // COMPOSANTS RÉUTILISABLES
    // ============================================

    // Carte indicateur pour le résumé
    const SummaryCard = ({ icon: Icon, label, value, color = 'gray', large = false }) => (
        <div className={`
            bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4
            ${large ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30' : 'border-gray-200 dark:border-gray-700'}
        `}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 text-${color}-500`} />
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{label}</span>
            </div>
            <p className={`
                font-bold font-mono
                ${large ? 'text-xl sm:text-2xl text-blue-700 dark:text-blue-300' : 'text-lg sm:text-xl text-gray-900 dark:text-white'}
            `}>
                {formatCFA(value)}
            </p>
        </div>
    );

    // Ligne de comptage
    const CountLine = ({ label, value, onChange, multiplier }) => (
        <div className="flex items-center justify-between py-2 px-2 sm:px-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
            <span className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 min-w-[60px] sm:min-w-[80px]">
                {label}
            </span>
            <div className="flex items-center gap-2 sm:gap-3">
                <Input
                    type="number"
                    min="0"
                    value={value}
                    onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                    className="w-14 sm:w-16 h-9 sm:h-10 text-center text-base sm:text-lg font-bold"
                />
                <span className="text-xs sm:text-sm text-gray-400 min-w-[70px] sm:min-w-[90px] text-right font-mono">
                    = {(value * multiplier).toLocaleString('fr-FR')} F
                </span>
            </div>
        </div>
    );

    // Carte de paiement électronique
    const PaymentCard = ({ icon: Icon, label, value, color }) => (
        <div className={`bg-${color}-50 dark:bg-${color}-950/30 border-2 border-${color}-200 dark:border-${color}-800 rounded-xl p-4`}>
            <div className="flex items-center gap-3">
                <div className={`p-2 bg-${color}-100 dark:bg-${color}-900 rounded-lg`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <div className="flex-1">
                    <p className={`text-xs sm:text-sm text-${color}-600 dark:text-${color}-400 font-medium`}>{label}</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white font-mono">
                        {formatCFA(value)}
                    </p>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-950">
                <RefreshCw className="h-10 w-10 animate-spin text-emerald-500" />
            </div>
        );
    }

    const varianceInfo = getVarianceStatus();

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-950 pb-24" data-testid="cash-closing-page">
            {/* ============================================ */}
            {/* HEADER */}
            {/* ============================================ */}
            <div className="bg-white dark:bg-gray-900 border-b px-4 py-4 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                            Clôture de Caisse
                        </h1>
                        <p className="text-sm text-gray-500">
                            {new Date().toLocaleDateString('fr-FR', { 
                                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                            })}
                        </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={fetchData}>
                        <RefreshCw className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-4 space-y-6">
                
                {/* ============================================ */}
                {/* ZONE 1 : RÉSUMÉ THÉORIQUE */}
                {/* ============================================ */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Résumé de clôture
                        </h2>
                    </div>
                    
                    {/* Grille des indicateurs */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {/* Fond de caisse */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-3 sm:p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <PiggyBank className="w-4 h-4 text-gray-500" />
                                <span className="text-xs sm:text-sm text-gray-500">Fond de caisse</span>
                            </div>
                            <Input
                                type="number"
                                value={fondDeCaisse}
                                onChange={(e) => setFondDeCaisse(parseInt(e.target.value) || 0)}
                                className="h-10 text-lg font-bold font-mono text-right border-gray-300"
                            />
                        </div>
                        
                        {/* Ventes espèces */}
                        <SummaryCard 
                            icon={Banknote} 
                            label="Ventes espèces" 
                            value={summary?.breakdown?.cash?.amount || 0}
                            color="green"
                        />
                        
                        {/* TOTAL THÉORIQUE - Plus grand */}
                        <SummaryCard 
                            icon={Calculator} 
                            label="TOTAL THÉORIQUE" 
                            value={getTheoreticalCash()}
                            large={true}
                        />
                        
                        {/* Carte bancaire */}
                        <SummaryCard 
                            icon={CreditCard} 
                            label="Carte bancaire" 
                            value={getCardTotal()}
                            color="purple"
                        />
                        
                        {/* Airtel Money */}
                        <SummaryCard 
                            icon={Smartphone} 
                            label="Airtel Money" 
                            value={getAirtelTotal()}
                            color="red"
                        />
                        
                        {/* Moov Money */}
                        <SummaryCard 
                            icon={Smartphone} 
                            label="Moov Money" 
                            value={getMoovTotal()}
                            color="blue"
                        />
                    </div>
                </section>

                {/* ============================================ */}
                {/* ZONE 2 : COMPTAGE DES ESPÈCES */}
                {/* ============================================ */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                            <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Comptage des espèces
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* PIÈCES */}
                        <Card className="bg-amber-50/50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-amber-800 dark:text-amber-300">
                                    <Coins className="w-5 h-5" />
                                    Pièces
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <CountLine label="25 F" value={coins.c25} onChange={(v) => setCoins({...coins, c25: v})} multiplier={25} />
                                <CountLine label="50 F" value={coins.c50} onChange={(v) => setCoins({...coins, c50: v})} multiplier={50} />
                                <CountLine label="100 F" value={coins.c100} onChange={(v) => setCoins({...coins, c100: v})} multiplier={100} />
                                <CountLine label="200 F" value={coins.c200} onChange={(v) => setCoins({...coins, c200: v})} multiplier={200} />
                                
                                {/* TOTAL PIÈCES */}
                                <div className="mt-4 pt-3 border-t-2 border-amber-300 dark:border-amber-700">
                                    <div className="flex items-center justify-between bg-amber-100 dark:bg-amber-900/50 rounded-lg p-3">
                                        <span className="font-bold text-amber-800 dark:text-amber-300">Total Pièces</span>
                                        <span className="text-xl sm:text-2xl font-black text-amber-700 dark:text-amber-200 font-mono">
                                            {formatCFA(getTotalCoins())}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* BILLETS */}
                        <Card className="bg-emerald-50/50 dark:bg-emerald-950/20 border-2 border-emerald-200 dark:border-emerald-800">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-emerald-800 dark:text-emerald-300">
                                    <Banknote className="w-5 h-5" />
                                    Billets
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <CountLine label="500 F" value={bills.b500} onChange={(v) => setBills({...bills, b500: v})} multiplier={500} />
                                <CountLine label="1 000 F" value={bills.b1000} onChange={(v) => setBills({...bills, b1000: v})} multiplier={1000} />
                                <CountLine label="2 000 F" value={bills.b2000} onChange={(v) => setBills({...bills, b2000: v})} multiplier={2000} />
                                <CountLine label="5 000 F" value={bills.b5000} onChange={(v) => setBills({...bills, b5000: v})} multiplier={5000} />
                                <CountLine label="10 000 F" value={bills.b10000} onChange={(v) => setBills({...bills, b10000: v})} multiplier={10000} />
                                
                                {/* TOTAL BILLETS */}
                                <div className="mt-4 pt-3 border-t-2 border-emerald-300 dark:border-emerald-700">
                                    <div className="flex items-center justify-between bg-emerald-100 dark:bg-emerald-900/50 rounded-lg p-3">
                                        <span className="font-bold text-emerald-800 dark:text-emerald-300">Total Billets</span>
                                        <span className="text-xl sm:text-2xl font-black text-emerald-700 dark:text-emerald-200 font-mono">
                                            {formatCFA(getTotalBills())}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* TOTAL ESPÈCES COMPTÉES */}
                    <Card className="mt-4 bg-gray-900 dark:bg-gray-800 border-2 border-gray-700">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-white/10 rounded-xl">
                                        <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-sm">TOTAL ESPÈCES COMPTÉES</p>
                                        <p className="text-xs text-gray-500">
                                            Pièces ({formatCFA(getTotalCoins())}) + Billets ({formatCFA(getTotalBills())})
                                        </p>
                                    </div>
                                </div>
                                <p className="text-3xl sm:text-4xl font-black text-white font-mono">
                                    {formatCFA(getTotalCash())}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* ============================================ */}
                {/* ZONE 3 : PAIEMENTS ÉLECTRONIQUES */}
                {/* ============================================ */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                            <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Paiements électroniques
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Carte bancaire */}
                        <div className="bg-purple-50 dark:bg-purple-950/30 border-2 border-purple-200 dark:border-purple-800 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                                    <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Carte bancaire</p>
                                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white font-mono">
                                        {formatCFA(getCardTotal())}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Airtel Money */}
                        <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                                    <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-red-600 dark:text-red-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">Airtel Money</p>
                                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white font-mono">
                                        {formatCFA(getAirtelTotal())}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Moov Money */}
                        <div className="bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Moov Money</p>
                                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white font-mono">
                                        {formatCFA(getMoovTotal())}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============================================ */}
                {/* ZONE 4 : RÉSULTAT FINAL */}
                {/* ============================================ */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-gray-200 dark:bg-gray-800 rounded-lg">
                            <Receipt className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Résultat final
                        </h2>
                    </div>

                    <Card className="border-2 border-gray-300 dark:border-gray-600">
                        <CardContent className="p-4 sm:p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                                {/* Théorique */}
                                <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl">
                                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                                        Total théorique espèces
                                    </p>
                                    <p className="text-2xl sm:text-3xl font-bold text-blue-700 dark:text-blue-300 font-mono">
                                        {formatCFA(getTheoreticalCash())}
                                    </p>
                                </div>

                                {/* Compté */}
                                <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-xl">
                                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
                                        Total espèces comptées
                                    </p>
                                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white font-mono">
                                        {formatCFA(getTotalCash())}
                                    </p>
                                </div>

                                {/* ÉCART */}
                                <div className={`text-center p-4 rounded-xl ${
                                    varianceInfo.color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                                    varianceInfo.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/50' :
                                    'bg-red-100 dark:bg-red-900/50'
                                }`}>
                                    <p className={`text-sm font-medium mb-1 ${
                                        varianceInfo.color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
                                        varianceInfo.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                                        'text-red-600 dark:text-red-400'
                                    }`}>
                                        Écart de caisse
                                    </p>
                                    <p className={`text-2xl sm:text-3xl font-black font-mono ${
                                        varianceInfo.color === 'emerald' ? 'text-emerald-700 dark:text-emerald-300' :
                                        varianceInfo.color === 'orange' ? 'text-orange-700 dark:text-orange-300' :
                                        'text-red-700 dark:text-red-300'
                                    }`}>
                                        {getVariance() === 0 ? '0 F' : 
                                         getVariance() > 0 ? `+${formatCFA(getVariance())}` : 
                                         `-${formatCFA(Math.abs(getVariance()))}`}
                                    </p>
                                    <p className={`text-xs mt-1 ${
                                        varianceInfo.color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
                                        varianceInfo.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                                        'text-red-600 dark:text-red-400'
                                    }`}>
                                        {varianceInfo.color === 'emerald' && '✓'} {varianceInfo.label}
                                    </p>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="mt-6">
                                <Label className="text-sm text-gray-500">Notes / Observations (optionnel)</Label>
                                <Textarea
                                    placeholder="Remarques éventuelles sur cette clôture..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="mt-2 h-20 resize-none"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </section>
            </div>

            {/* ============================================ */}
            {/* BARRE D'ACTION STICKY */}
            {/* ============================================ */}
            <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t-2 shadow-2xl p-4 z-50">
                <div className="max-w-6xl mx-auto">
                    {/* Résumé mobile */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3 sm:mb-0">
                        <div className="hidden sm:flex items-center gap-4 text-sm">
                            <span className="text-gray-500">
                                Théo: <b className="text-gray-900 dark:text-white font-mono">{formatCFA(getTheoreticalCash())}</b>
                            </span>
                            <span className="text-gray-500">
                                Compté: <b className="text-gray-900 dark:text-white font-mono">{formatCFA(getTotalCash())}</b>
                            </span>
                            <span className={`font-bold ${
                                varianceInfo.color === 'emerald' ? 'text-emerald-600' :
                                varianceInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'
                            }`}>
                                Écart: {getVariance() >= 0 ? '+' : ''}{formatCFA(getVariance())}
                            </span>
                        </div>

                        {/* Boutons */}
                        <div className="flex gap-3 w-full sm:w-auto">
                            <Button 
                                variant="outline" 
                                className="flex-1 sm:flex-none h-12 sm:h-11"
                                onClick={fetchData}
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={() => setConfirmDialogOpen(true)}
                                disabled={!hasCountedSomething()}
                                className={`flex-1 sm:flex-none h-12 sm:h-11 px-6 sm:px-8 text-base font-bold shadow-lg ${
                                    hasCountedSomething() 
                                        ? 'bg-emerald-600 hover:bg-emerald-700' 
                                        : 'bg-gray-300 cursor-not-allowed'
                                }`}
                                data-testid="submit-closing-btn"
                            >
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Soumettre la clôture
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================ */}
            {/* DIALOG CONFIRMATION */}
            {/* ============================================ */}
            <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <DialogContent className="max-w-md mx-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <AlertCircle className="h-5 w-5 text-orange-500" />
                            Confirmer la clôture
                        </DialogTitle>
                        <DialogDescription>
                            Vérifiez les informations avant de valider définitivement
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Récap rapide */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-xl text-center">
                                <p className="text-xs text-blue-600 mb-1">Théorique</p>
                                <p className="text-lg font-bold font-mono">{formatCFA(getTheoreticalCash())}</p>
                            </div>
                            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-center">
                                <p className="text-xs text-gray-500 mb-1">Compté</p>
                                <p className="text-lg font-bold font-mono">{formatCFA(getTotalCash())}</p>
                            </div>
                        </div>

                        {/* Écart */}
                        <div className={`p-4 rounded-xl text-center ${
                            varianceInfo.color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                            varianceInfo.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/50' :
                            'bg-red-100 dark:bg-red-900/50'
                        }`}>
                            <p className="text-xs text-gray-600 mb-1">ÉCART DE CAISSE</p>
                            <p className={`text-3xl font-black font-mono ${
                                varianceInfo.color === 'emerald' ? 'text-emerald-700' :
                                varianceInfo.color === 'orange' ? 'text-orange-700' : 'text-red-700'
                            }`}>
                                {getVariance() >= 0 ? '+' : ''}{formatCFA(getVariance())}
                            </p>
                            <p className={`text-xs mt-1 ${
                                varianceInfo.color === 'emerald' ? 'text-emerald-600' :
                                varianceInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'
                            }`}>
                                {varianceInfo.label}
                            </p>
                        </div>

                        {/* Avertissement si écart */}
                        {getVariance() !== 0 && (
                            <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
                                getVariance() < 0 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
                            }`}>
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>
                                    {getVariance() < 0 
                                        ? `Attention : Il manque ${formatCFA(Math.abs(getVariance()))} dans la caisse.`
                                        : `Il y a un excédent de ${formatCFA(getVariance())} dans la caisse.`
                                    }
                                </span>
                            </div>
                        )}

                        <p className="text-sm text-gray-500 text-center">
                            Clôture effectuée par <b>{user?.username || user?.name || 'Utilisateur'}</b>
                        </p>
                    </div>
                    
                    <DialogFooter className="gap-3 sm:gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setConfirmDialogOpen(false)}
                            className="flex-1 sm:flex-none"
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleClosing}
                            disabled={submitting}
                            className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700"
                        >
                            {submitting ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Envoi...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Confirmer
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashClosingPage;
